import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../../components/sidebars/sidebar_reception';
import TopNav from '../../../../components/TopNav_hotelStaff';
import StatisticsCards from '../../../../components/StatisticsCard_staff';
import axios from 'axios';
import { 
  Bar, 
  Doughnut, 
  Line,
  Pie 
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

// Modern color palette
const CHART_COLORS = {
  primary: '#6366F1',
  secondary: '#EC4899',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#3B82F6',
  purple: '#8B5CF6',
  indigo: '#4F46E5',
  pink: '#EC4899',
  blue: '#3B82F6',
  teal: '#14B8A6',
  orange: '#F97316',
};

const GRADIENT_COLORS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
];

// Move monthNames to the top level
const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [roomData, setRoomData] = useState(null);

  // User profile info from localStorage
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const hotel_code = userProfile ? userProfile.hotel_code : "eHT5678042024";

  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  // Fetch all analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        
        // Fetch booking analytics
        const bookingResponse = await axios.get(
          `${baseURL}/hotel/staff/reception/bookings_analytics.php?hotel_code=${hotel_code}`
        );
        setBookingData(bookingResponse.data);

        // Fetch guest analytics
        const guestResponse = await axios.get(
          `${baseURL}/hotel/staff/reception/guest_analytics.php?hotel_code=${hotel_code}`
        );
        setGuestData(guestResponse.data);

        // Fetch room performance (with error handling)
        try {
          const roomResponse = await axios.get(
            `${baseURL}/hotel/staff/reception/room_performance.php?hotel_code=${hotel_code}`
          );
          setRoomData(roomResponse.data);
        } catch (roomError) {
          console.error('Error fetching room data:', roomError);
          // Set empty room data structure
          setRoomData({
            success: false,
            room_performance: [],
            type_summary: [],
            status_distribution: []
          });
        }

      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [hotel_code]);

  // Modern chart options
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
    },
    scales: {
      x: { 
        grid: { 
          display: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: { 
        grid: { 
          color: 'rgba(0, 0, 0, 0.05)' 
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
    },
    maintainAspectRatio: false,
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        }
      },
    },
    maintainAspectRatio: false,
  };

  const lineOptions = {
    ...barOptions,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
    },
    scales: {
      x: { 
        grid: { 
          display: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: { 
        grid: { 
          color: 'rgba(0, 0, 0, 0.05)' 
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
    },
  };

  // Prepare chart data from API responses
  const revenueTrendChart = bookingData ? {
    labels: bookingData.monthly_trend.map(item => {
      const [year, month] = item.month.split('-');
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    }),
    datasets: [
      {
        label: 'Monthly Revenue (₦)',
        data: bookingData.monthly_trend.map(item => item.monthly_revenue),
        backgroundColor: CHART_COLORS.primary,
        borderColor: CHART_COLORS.primary,
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: CHART_COLORS.primary,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  } : null;

  const bookingSourceChart = bookingData ? {
    labels: bookingData.source_analysis.map(item => item.source),
    datasets: [
      {
        data: bookingData.source_analysis.map(item => item.percentage),
        backgroundColor: [
          CHART_COLORS.primary,
          CHART_COLORS.secondary,
          CHART_COLORS.success,
          CHART_COLORS.warning,
          CHART_COLORS.info,
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 15,
      },
    ],
  } : null;

  const roomPerformanceChart = bookingData ? {
    labels: bookingData.room_performance.map(item => item.room_type),
    datasets: [
      {
        label: 'Total Revenue (₦)',
        data: bookingData.room_performance.map(item => item.total_revenue),
        backgroundColor: CHART_COLORS.success,
        borderColor: CHART_COLORS.success,
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Booking Count',
        data: bookingData.room_performance.map(item => item.bookings_count),
        backgroundColor: CHART_COLORS.warning,
        borderColor: CHART_COLORS.warning,
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  } : null;

  const guestDemographicsChart = guestData ? {
    labels: guestData.demographics.map(item => 
      `${item.gender}${item.nationality ? ` - ${item.nationality}` : ''}`
    ),
    datasets: [
      {
        data: guestData.demographics.map(item => item.guest_count),
        backgroundColor: [
          CHART_COLORS.primary,
          CHART_COLORS.secondary,
          CHART_COLORS.success,
          CHART_COLORS.warning,
          CHART_COLORS.info,
          CHART_COLORS.purple,
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 15,
      },
    ],
  } : null;

  const highValueGuestsChart = guestData ? {
    labels: guestData.high_value_guests.map(item => 
      `${item.first_name} ${item.last_name}`
    ),
    datasets: [
      {
        label: 'Lifetime Value (₦)',
        data: guestData.high_value_guests.map(item => item.lifetime_value),
        backgroundColor: GRADIENT_COLORS[0],
        borderColor: CHART_COLORS.purple,
        borderWidth: 0,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  } : null;

  // Get top 10 repeat guests
  const topRepeatGuests = guestData?.repeat_guests?.slice(0, 10) || [];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '100%',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Statistics Cards Section */}
        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>

        {/* Charts Grid - Improved spacing and alignment */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Revenue Trend Chart */}
          <Grid item xs={12} lg={8}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                📈 Monthly Revenue Trend
              </Typography>
              {revenueTrendChart ? (
                <Box sx={{ height: 320 }}>
                  <Line 
                    data={revenueTrendChart} 
                    options={lineOptions}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Booking Sources */}
          <Grid item xs={12} lg={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                🎯 Booking Sources
              </Typography>
              {bookingSourceChart ? (
                <Box sx={{ height: 320 }}>
                  <Doughnut 
                    data={bookingSourceChart} 
                    options={doughnutOptions}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Room Performance */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                🏨 Room Performance
              </Typography>
              {roomPerformanceChart ? (
                <Box sx={{ height: 320 }}>
                  <Bar 
                    data={roomPerformanceChart} 
                    options={barOptions}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Guest Demographics */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                👥 Guest Demographics
              </Typography>
              {guestDemographicsChart ? (
                <Box sx={{ height: 320 }}>
                  <Pie 
                    data={guestDemographicsChart} 
                    options={doughnutOptions}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* High Value Guests - Full Width */}
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 450, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                ⭐ High Value Guests
              </Typography>
              {highValueGuestsChart ? (
                <Box sx={{ height: 370 }}>
                  <Bar 
                    data={highValueGuestsChart} 
                    options={{
                      ...barOptions,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 370 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Additional Insights Section */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Repeat Guests - Top 10 Only */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                minHeight: 400,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                🔄 Top 10 Repeat Guests
              </Typography>
              {topRepeatGuests.length > 0 ? (
                <Stack spacing={2}>
                  {topRepeatGuests.map((guest, index) => (
                    <Card 
                      key={index} 
                      elevation={0}
                      sx={{ 
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              mr: 2,
                              bgcolor: GRADIENT_COLORS[index % GRADIENT_COLORS.length]
                            }}
                          >
                            {guest.first_name?.[0]}{guest.last_name?.[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {guest.first_name} {guest.last_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip 
                                label={`${guest.total_bookings} Bookings`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                              <Chip 
                                label={`₦${parseInt(guest.total_spent).toLocaleString()}`} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Last visit: {new Date(guest.last_booking_date).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    No repeat guests data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Geographic Distribution */}
          {/* Geographic Distribution */}
<Grid item xs={12} md={6}>
  <Paper 
    elevation={0} 
    sx={{ 
      p: 3, 
      borderRadius: 3,
      background: 'white',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      minHeight: 400,
    }}
  >
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
      🌍 Guest Geographic Distribution
    </Typography>
    
    {/* Debug info - remove this after testing */}
    {guestData && console.log('Guest Data:', guestData)}
    {guestData && guestData.geo_distribution && console.log('Geo Distribution:', guestData.geo_distribution)}
    
    {guestData && guestData.geo_distribution && guestData.geo_distribution.length > 0 ? (
      <Stack spacing={2}>
        {guestData.geo_distribution.slice(0, 10).map((location, index) => (
          <Card 
            key={index}
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              background: GRADIENT_COLORS[index % GRADIENT_COLORS.length],
              color: 'white',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  {location.country || location.nationality || 'Unknown Country'}
                </Typography>
                <Chip 
                  label={`${location.guest_count || location.count || 0} guests`} 
                  size="small" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    ) : (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body2" color="text.secondary">
          {guestData ? 'No geographic data available' : 'Loading geographic data...'}
        </Typography>
        {/* Fallback demo data for testing */}
        {(!guestData || !guestData.geo_distribution) && (
          <Stack spacing={2} sx={{ mt: 3 }}>
            {[
              { country: 'United States', guests: 15 },
              { country: 'United Kingdom', guests: 8 },
              { country: 'Canada', guests: 6 },
              { country: 'Australia', guests: 4 },
              { country: 'Germany', guests: 3 }
            ].map((location, index) => (
              <Card 
                key={index}
                elevation={0}
                sx={{ 
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  background: GRADIENT_COLORS[index % GRADIENT_COLORS.length],
                  color: 'white',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={600}>
                      {location.country}
                    </Typography>
                    <Chip 
                      label={`${location.guests} guests`} 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    )}
  </Paper>
</Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;